import cx from 'classnames'
import { FC } from 'react'
import { HeroProps } from 'src/components/Hero/types'
import KeyFigure from 'src/components/KeyFigure'
import {
  fadeInBottomRight,
  separatorAnimation,
  slideInTopLeft,
  textAnimation,
} from 'src/hooks/useAppear'
import { colors } from 'src/theme'
import { MotionMask, Separator } from 'src/theme/styled'
import { v4 } from 'uuid'

import * as SC from './styled'

const Hero: FC<HeroProps> = ({
  className,
  htmlTag = 'hgroup',
  htmlTitleTag = 'h1',
  title,
  richTextProps,
  pictureProps,
  keyFigures,
  roundedTagProps,
  actionButtonProps,
  color = colors.blueLight,
}) => {
  const keyFiguresArray = keyFigures?.[0].split(' ')
  const [big, ...medium] = keyFiguresArray ? keyFiguresArray : []

  const { animations } = useHeroDefaultMotion()
  const _uuid = v4()

  return (
    <SC.Container
      as={htmlTag}
      className={cx('Hero', className)}
      $color={color}
      key={_uuid}
    >
      <SC.PictureContainer {...animations.pictureContainer}>
        {pictureProps && (
          <SC._Picture {...pictureProps} {...animations.picture} />
        )}
      </SC.PictureContainer>
      <SC.Content>
        {roundedTagProps && (
          <MotionMask>
            <SC._MotionWrapper {...animations.tag}>
              <SC._RoundedTag {...roundedTagProps} />
            </SC._MotionWrapper>
          </MotionMask>
        )}
        {title && (
          <SC.Title as={htmlTitleTag}>
            <SC._MotionWrapper {...animations.title}>{title}</SC._MotionWrapper>
          </SC.Title>
        )}
        {!keyFigures?.[0] && (richTextProps || actionButtonProps) && (
          <Separator aria-hidden role="separator" {...animations.separator} />
        )}
        {richTextProps && (
          <MotionMask>
            <SC._MotionWrapper {...animations.text}>
              <SC._RichText {...richTextProps} />
            </SC._MotionWrapper>
          </MotionMask>
        )}
        {actionButtonProps && (
          <MotionMask>
            <SC._MotionWrapper {...animations.action}>
              <SC._ActionButton {...actionButtonProps} />
            </SC._MotionWrapper>
          </MotionMask>
        )}
        {keyFigures && keyFigures.length > 0 && (
          <>
            <Separator aria-hidden role="separator" {...animations.separator} />
            {(big || keyFigures?.[1]) && (
              <MotionMask>
                <SC.KeyFigures {...animations.keyFigure}>
                  {big && (
                    <KeyFigure
                      withMotion
                      delay={1.15}
                      unit={medium}
                      value={big}
                    />
                  )}
                  {keyFigures?.[1] && <span>{keyFigures?.[1]}</span>}
                </SC.KeyFigures>
              </MotionMask>
            )}
          </>
        )}
      </SC.Content>
    </SC.Container>
  )
}

const useHeroDefaultMotion = () => ({
  animations: {
    pictureContainer: slideInTopLeft({ delay: 0.2, duration: 1.3 }),
    picture: fadeInBottomRight({ delay: 0.2, duration: 1.3 }),
    separator: separatorAnimation({ delay: 0.5, duration: 1 }),
    tag: textAnimation({ delay: 0.7 }),
    title: textAnimation({ delay: 0.8 }),
    text: textAnimation({ delay: 1 }),
    action: textAnimation({ delay: 1.2 }),
    keyFigure: textAnimation({ delay: 1.15 }),
  },
})

export default Hero
